import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useHistory, useLocation } from "react-router-dom";
import { isEmpty } from "lodash";

import { withStyles } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";

import { Button, GridContainerWithSpacing as GridContainer, GridItemWithSpacing as GridItem } from "~/shared/components/MuiComponents.jsx";

import KickClient from "~/visits/utils/api.js";
import {
  ConfirmAddressModal,
  CouldntVerifyAddressModal,
  MissingAddressComponentsModal,
  ShippingInputs,
  StateNotSupportedModal,
} from "./paymentComponents.jsx";
import { orderDetailsFormValidationSchema } from "./paymentHelpers.js";
import { googleValidateAddress } from "~/shared/addressValidation";

import { usStatesList } from "~/shared/widgets/autocompleteUSStates.jsx";
import {
  clearIsEditingPendingVisitPharmacy,
  getIsEditingPendingVisitPharmacy,
  getLatestAddress,
  getOrderDetailsData,
  setOrderDetailsData,
  getVisitPhone,
  getVisitProduct,
  getVisitID,
} from "~/visits/utils/visitCookies.js";
import {
  getGoogleAPIKey,
  isMailOrderType,
  supportedUSStates,
} from "~/shared/utils/sharedUtils";

import { kickNavy, kickAmaOrange } from "~/shared/styles/baseStyles.js";
import consultationStyles from "~/visits/styles/consultationStyles.js";

const latestAddress = getLatestAddress();

let initialFormStateValues = {
  address1: "",
  address2: "",
  city: "",
  zip: "",
  usState: "",
  phone: getVisitPhone(),
  smsConsent: true,
};

const OrderDetails = (props) => {
  const { classes } = props;
  const { consultationType } = props.match.params;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");

  const history = useHistory();

  const googleAPIKey = getGoogleAPIKey();
  const savedOrderDetailsData = getOrderDetailsData(checkoutSessionID);

  // use saved order details if they exist (from going 'back' from payment details page)
  if (!isEmpty(savedOrderDetailsData)) {
    initialFormStateValues = {
      ...initialFormStateValues,
      ...savedOrderDetailsData,
      usState: usStatesList.find(
        (v) => v.abbreviation == savedOrderDetailsData.usState
      ),
    };
  } else if (!isEmpty(latestAddress)) {
    // otherwise try to use latest address (like in refills)
    initialFormStateValues = {
      ...initialFormStateValues,
      ...latestAddress,
      usState: usStatesList.find(
        (v) => v.abbreviation == latestAddress.usState
      ),
    };
  }

  // state variables
  const [showAddressModal, setShowAddressModal] = useState({});
  const [
    { address1, address2, city, usState, zip, phone, smsConsent },
    setFormState,
  ] = useState(initialFormStateValues);
  const [formIsSubmitting, setFormIsSubmitting] = useState(false);

  const validateAddress = async (apiKey, address) => {
    const fixAddressModal = await googleValidateAddress(apiKey, address);
    setFormIsSubmitting(false);
    if (fixAddressModal) {
      const { modalName, modalData } = fixAddressModal;
      setShowAddressModal({ modalName, modalData });
      setFormState(address);
    } else {
      finishOrderDetailsSection(address);
    }
  };

  const finishOrderDetailsSection = async (orderDetails) => {
    setShowAddressModal({}); // clear address modal
    setFormState(orderDetails);
    setFormIsSubmitting(false);

    // double check that google address validation didn't set an unsupported state
    if (
      isMailOrderType(getVisitProduct(checkoutSessionID)) &&
      !supportedUSStates.includes(orderDetails.usState.abbreviation)
    ) {
      setShowAddressModal({
        modalName: "stateNotSupported",
        modalData: {
          unsupportedState: orderDetails.usState.name,
        },
      });
      return;
    }

    const visitID = getVisitID(checkoutSessionID);
    setOrderDetailsData(
      {
        ...orderDetails,
        usState: orderDetails.usState.abbreviation,
      },
      checkoutSessionID
    );
    await KickClient.newAuthenticatedClient().put(
      `/visits/${visitID}/update-shipping-address`,
      {
        address1: orderDetails.address1,
        address2: orderDetails.address2,
        city: orderDetails.city,
        usState: orderDetails.usState.abbreviation,
        zip: orderDetails.zip,
      }
    );

    // When editing only the shipping address of a pending visit (e.g. an INF visit reached via
    // setCookiesAndRedirectToEditShippingAddress), there's no checkout to continue — save the
    // address and return to the portal rather than advancing the new-visit flow.
    if (getIsEditingPendingVisitPharmacy(checkoutSessionID)) {
      clearIsEditingPendingVisitPharmacy(checkoutSessionID);
      window.location.href = "/me/portal";
      return;
    }

    history.push(
      `/visits/${consultationType}/complete?checkoutSessionID=${checkoutSessionID}`
    );
  };

  // form props
  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(orderDetailsFormValidationSchema),
  });

  return (
    <div className={`${classes.containerWithSpacing} payment-section`}>
      <GridContainer justifyContent="center">
        <GridItem xs={12} md={8} justifyContent="center">
          <div className={classes.paymentForm}>
            <form
              style={{ width: "100%", marginTop: "40px" }}
              onSubmit={handleSubmit(async (data) => {
                console.log("[OrderDetails onSubmit] errors: ", errors);
                console.log(data);
              })}
            >
              <p className={classes.subtitle}>Confirm Shipping Address</p>

              <StateNotSupportedModal
                showModal={showAddressModal.modalName === "stateNotSupported"}
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                consultationType={consultationType}
              />

              <ConfirmAddressModal
                showModal={showAddressModal.modalName === "confirmAddress"}
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                submit={(data) => {
                  if (data) {
                    validateAddress(googleAPIKey, {
                      ...getValues(),
                      ...data,
                    });
                  } else {
                    finishOrderDetailsSection(getValues());
                  }
                }}
              />

              <MissingAddressComponentsModal
                showModal={
                  showAddressModal.modalName === "missingAddressComponents"
                }
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                submit={(data) => {
                  if (data) {
                    validateAddress(googleAPIKey, {
                      ...getValues(),
                      ...data,
                    });
                  } else {
                    finishOrderDetailsSection(getValues());
                  }
                }}
              />

              <CouldntVerifyAddressModal
                showModal={
                  showAddressModal.modalName === "couldntVerifyAddress"
                }
                setShowModal={setShowAddressModal}
                modalData={showAddressModal.modalData}
                submit={() => {
                  finishOrderDetailsSection(getValues());
                }}
              />

              <ShippingInputs
                classes={classes}
                errors={errors}
                control={control}
                address1={address1}
                address2={address2}
                city={city}
                usState={usState}
                zip={zip}
                phone={phone}
                smsConsent={smsConsent}
              />

              <Button
                onClick={async (e) => {
                  setFormIsSubmitting(true);
                  e.preventDefault();
                  const valid = await trigger([
                    "address1",
                    "city",
                    "usState",
                    "zip",
                    "phone",
                  ]);
                  if (valid) {
                    const formValues = getValues();
                    const stateAbbr = formValues.usState?.abbreviation;
                    // check if state is supported
                    if (
                      isMailOrderType(getVisitProduct(checkoutSessionID)) &&
                      !supportedUSStates.includes(stateAbbr)
                    ) {
                      setFormIsSubmitting(false);
                      setShowAddressModal({
                        modalName: "stateNotSupported",
                        modalData: {
                          unsupportedState: formValues.usState.name,
                        },
                      });
                    } else {
                      await validateAddress(googleAPIKey, formValues);
                    }
                  } else {
                    setFormIsSubmitting(false);
                  }
                }}
                className={classes.confirmAndPayButton}
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  textTransform: "unset",
                  backgroundColor: kickAmaOrange,
                  color: kickNavy,
                }}
              >
                Next
                {formIsSubmitting ? (
                  <CircularProgress
                    size="large"
                    style={{
                      position: "relative",
                      left: "24px",
                      top: "1px",
                      color: kickNavy,
                    }}
                  />
                ) : null}
              </Button>
            </form>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default withStyles(consultationStyles)(OrderDetails);
